/**
 * QuotaExceededDialog — modal contextual quando o usuário tenta uma ação
 * que estoura o limite do plano (upload muito grande, X projetos, etc).
 *
 * Em vez de só mostrar um toast "Storage cheio" e deixar o usuário se
 * virar pra achar onde comprar, abrimos um modal que:
 *   1. Diz EXATAMENTE quanto faltou e por quê
 *   2. Sugere o MENOR pack que resolveria (sem oversell)
 *   3. Mostra opção de planos como alternativa (quem precisa de muito
 *      espaço fixa, melhor mudar de plano)
 *   4. 1-clique pra checkout (BillingCheckoutDialog interno)
 *
 * Mounted at the app root via QuotaNudgeMount — qualquer hook/componente
 * dispara via useQuotaStore.openStorageNudge(bytesNeeded).
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { AlertTriangle, HardDrive, ArrowRight, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { Progress } from '@/components/ui/progress';
import { useQuotaStore } from '@/store/quotaStore';
import { useStoragePacks, usePlans } from '@/api/queries';
import { usePlan } from '@/hooks/usePlan';
import { formatPriceBRL, formatStorage } from '@my360/shared';
import { BillingCheckoutDialog, type BillingSku } from './BillingCheckoutDialog';
import { purchasesAllowed } from '@/lib/purchases';
import { cn } from '@/lib/utils';

export function QuotaExceededDialog() {
  const { t } = useTranslation();
  const { open, bytesNeeded, reason, close } = useQuotaStore();
  const { storage, slug } = usePlan();
  const { data: catalog } = useStoragePacks();
  const { data: plansData } = usePlans();

  const [checkoutSku, setCheckoutSku] = useState<BillingSku | null>(null);

  // Após confirmar checkout, fechar o nudge
  const handleSuccess = () => {
    close();
  };

  // Acha o menor pack que cobriria o gap (bytesNeeded). Se nenhum cabe
  // (faltam centenas de GB), sugere o maior + dica de mudar plano.
  const recommendedPack = useMemo(() => {
    const packs = (catalog?.data ?? []).slice().sort((a, b) => a.bonusBytes - b.bonusBytes);
    if (packs.length === 0) return null;
    return packs.find((p) => p.bonusBytes >= bytesNeeded) ?? packs[packs.length - 1];
  }, [catalog?.data, bytesNeeded]);

  // Plan target para upgrade — Pro é a recomendação padrão (o Beta foi
  // aposentado da venda aberta; virou cupom).
  const upgradeTarget = useMemo(() => {
    const all = plansData?.data ?? [];
    return all.find((p) => p.slug === 'pro');
  }, [plansData?.data]);

  const handleBuyPack = () => {
    if (!recommendedPack) return;
    setCheckoutSku({
      kind: 'pack',
      slug: recommendedPack.slug,
      name: recommendedPack.name,
      priceCents: recommendedPack.priceCents,
      bonusBytes: recommendedPack.bonusBytes,
    });
  };

  const handleUpgrade = () => {
    if (!upgradeTarget) return;
    setCheckoutSku({
      kind: 'plan',
      slug: upgradeTarget.slug,
      name: upgradeTarget.name,
      priceCents: upgradeTarget.price,
    });
  };

  const isFree = slug === 'free';
  // App nativo (Google Play): o aviso de limite continua — é informação —
  // mas packs, preços e upgrade somem (nada de venda fora do Play Billing).
  const canBuy = purchasesAllowed();

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={(o) => !o && close()}
        size="sm"
        title={
          <span className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-warning)]/15 text-[var(--color-warning)]">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <span className="flex flex-col">
              <span
                className="text-base font-semibold tracking-tight"
                style={{ color: 'var(--color-text)' }}
              >
                {t('billingUi:quota.title')}
              </span>
              <span
                className="text-[11px] font-normal"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {t('billingUi:quota.subtitle')}
              </span>
            </span>
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={close}>
              {t('billingUi:quota.later')}
            </Button>
            {canBuy && recommendedPack && (
              <Button variant="primary" onClick={handleBuyPack}>
                <Plus className="h-4 w-4 mr-1.5" />
                {t('billingUi:quota.buyPack')}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-4">
          {reason && (
            <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/8 p-3 text-[12px] text-[var(--color-text)] leading-relaxed">
              {reason}
            </div>
          )}

          {/* Estado atual */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-[var(--color-text-muted)] font-medium">
                <HardDrive className="h-3.5 w-3.5" />
                {t('billingUi:quota.currentStorage')}
              </span>
              <span className="font-mono tabular-nums font-semibold text-[var(--color-text)]">
                {storage.usedLabel} / {storage.limitLabel}
              </span>
            </div>
            <Progress
              value={storage.percent}
              variant={storage.critical ? 'destructive' : 'warning'}
              className="h-1.5"
            />
            {bytesNeeded > 0 && (
              <p className="text-[11px] text-[var(--color-text-muted)] mt-1">
                {t('billingUi:quota.needsMore', { amount: formatStorage(bytesNeeded) })}
              </p>
            )}
          </div>

          {/* Pack recomendado */}
          {canBuy && recommendedPack && (
            <button
              type="button"
              onClick={handleBuyPack}
              className={cn(
                'w-full flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 transition-all',
                'border-[var(--color-primary)] bg-[var(--color-primary)]/8 hover:bg-[var(--color-primary)]/15',
              )}
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] text-white flex items-center justify-center shadow-md">
                  <Plus className="h-5 w-5" strokeWidth={3} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-[var(--color-text)]">
                    +{recommendedPack.bonusBytes / 1024 ** 3} {t('billingUi:common.gb')}
                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-primary)]">
                      {t('billingUi:quota.recommended')}
                    </span>
                  </div>
                  <div className="text-[11px] text-[var(--color-text-muted)]">
                    {t('billingUi:quota.resolveNow')}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-mono font-bold text-[var(--color-text)]">
                  {formatPriceBRL(recommendedPack.priceCents)}
                </div>
                <div className="text-[10px] text-[var(--color-text-muted)]">{t('billingUi:common.perMonth')}</div>
              </div>
            </button>
          )}

          {/* Alternativa: upgrade de plano (Free especialmente) */}
          {canBuy && (isFree || upgradeTarget) && upgradeTarget && (
            <motion.button
              initial={{ opacity: 0.7 }}
              whileHover={{ opacity: 1 }}
              type="button"
              onClick={handleUpgrade}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-4 py-3 hover:border-[var(--color-accent)]/40 transition-all"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-[var(--color-accent)]" />
                <div className="text-left">
                  <div className="text-[12px] font-semibold text-[var(--color-text)]">
                    {t('billingUi:quota.upgradeTo', { name: upgradeTarget.name })}
                  </div>
                  <div className="text-[10px] text-[var(--color-text-muted)]">
                    {t('billingUi:quota.upgradeDesc')}
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-[var(--color-text-muted)] shrink-0" />
            </motion.button>
          )}

          {canBuy && (
            <p className="text-[10px] text-center text-[var(--color-text-muted)] leading-relaxed">
              {t('billingUi:quota.afterPayment')}
            </p>
          )}
        </div>
      </ResponsiveDialog>

      {canBuy && (
        <BillingCheckoutDialog
          open={!!checkoutSku}
          onOpenChange={(o) => !o && setCheckoutSku(null)}
          sku={checkoutSku}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}

/**
 * Small wrapper to mount the dialog at the root WITHOUT having to pass
 * props down — it self-subscribes to useQuotaStore. Mount once in main.
 */
export function QuotaNudgeMount() {
  return <QuotaExceededDialog />;
}
